import { type IndexUiState } from 'instantsearch.js/es/types/ui-state'

/**
 * Query object for URL query string.
 * Explicit keys are used to remove unused parameter from the URL search parameters.
 */
export type ProductSearchAlgoliaRouteState = {
  category: string | undefined
  discountInPercent: { max?: string; min?: string } | undefined
  pageNumber: number | undefined
  priceMax: string | undefined
  priceMin: string | undefined
  q: string | undefined
  ratingMax: string | undefined
  ratingMin: string | undefined
  refinementList: Partial<Record<string, string[]>> | undefined
  sortBy: string | undefined
}

const PAIR = 2

const setPriceFromState = (
  route: ProductSearchAlgoliaRouteState,
  state: IndexUiState,
): void => {
  const { range } = state
  if (!range?.price) {
    return
  }

  const [priceMin, priceMax] = range.price.split(':', PAIR)

  route.priceMin = priceMin || undefined
  route.priceMax = priceMax || undefined
}

const setRatingFromState = (
  route: ProductSearchAlgoliaRouteState,
  state: IndexUiState,
): void => {
  const { range } = state
  if (!range?.averageRating) {
    return
  }

  const [ratingMin, ratingMax] = range.averageRating.split(':', PAIR)

  route.ratingMin = ratingMin || undefined
  route.ratingMax = ratingMax || undefined
}
const setDiscountFromState = (
  route: ProductSearchAlgoliaRouteState,
  state: IndexUiState,
): void => {
  const { range } = state

  if (!range?.discountInPercent) {
    return
  }

  const [discountMin, discountMax] = range.discountInPercent.split(':', PAIR)

  route.discountInPercent = {
    max: discountMax || undefined,
    min: discountMin || undefined,
  }
}
const setRefinementListFromState = (
  route: ProductSearchAlgoliaRouteState,
  state: IndexUiState,
): void => {
  const { refinementList } = state

  if (!refinementList) {
    return
  }

  /**
   * Get the `refinementList.primaryCategory` and map it to a category URL search parameter.
   * This is to keep the URL as it currently behaves in Fock.
   */
  const { primaryCategory, ...restRefinementList } = refinementList

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- this is wrongly typed in `RefinementListWidgetDescription` since this key may be absent
  route.category = primaryCategory?.at(-1)
  route.refinementList = restRefinementList
}

/**
 * Transforms an index `ui-state` representation into `routeState`.
 * It receives an object that contains the index UI state of all the widgets on the page.
 * See https://www.algolia.com/doc/api-reference/widgets/instantsearch/react/#widget-param-routing.
 */
export const productSearchAlgoliaIndexUiStateToRoute = (
  indexUiState: IndexUiState,
): ProductSearchAlgoliaRouteState => {
  /**
   * During navigation from the SERP to PDP via NextLink client routing, the `uiState` for indexes is cleared.
   * In this scenario, `uiState` equals `{"": {}}`.
   * This prevents stale URL parameters and avoids Algolia timeouts by unmounting components.
   *
   * https://github.com/algolia/instantsearch/blob/4769e20c1581ba7b4436b0da0dc9dd03cba3ef38/packages/react-instantsearch-core/src/lib/useInstantSearchApi.ts#L232-L238
   */
  const { page: pageNumber, query, sortBy } = indexUiState
  const routeState: ProductSearchAlgoliaRouteState = {
    category: undefined,
    discountInPercent: undefined,
    pageNumber,
    priceMax: undefined,
    priceMin: undefined,
    /* eslint-disable-next-line id-length -- query param */
    q: query,
    ratingMax: undefined,
    ratingMin: undefined,
    refinementList: undefined,
    sortBy,
  }

  setPriceFromState(routeState, indexUiState)
  setRatingFromState(routeState, indexUiState)
  setRefinementListFromState(routeState, indexUiState)
  setDiscountFromState(routeState, indexUiState)

  return routeState
}
